import type {
  ApiLimitType,
} from './api/types';

export const APP_CODE_NAME = 'A';
export const APP_NAME = process.env.APP_NAME || `Web ${APP_CODE_NAME}`;
export const RELEASE_DATETIME = process.env.RELEASE_DATETIME;

export const BASE_URL = process.env.BASE_URL;
export const CLIENT_DIAGNOSTICS_HOST = (process.env.CLIENT_DIAGNOSTICS_ENDPOINT || '').replace(/\/$/, '');
export const IS_CLIENT_DIAGNOSTICS_ENABLED = Boolean(CLIENT_DIAGNOSTICS_HOST);
export const SSG_ATTR_HOST = (process.env.SSG_ATTR_ENDPOINT || '').replace(/\/$/, '');
export const IS_SSG_ATTR_ENABLED = Boolean(SSG_ATTR_HOST);
export const ACCOUNT_QUERY = 'account';

export const GLOBAL_SUGGESTED_CHANNELS_ID = 'global';
export const IS_TEST = process.env.APP_ENV === 'test';
export const IS_BETA = process.env.APP_ENV === 'staging';
export const IS_PACKAGED_ELECTRON = process.env.IS_PACKAGED_ELECTRON;

export const DEBUG = process.env.APP_ENV !== 'production';
export const DEBUG_MORE = false;
export const STRICTERDOM_ENABLED = DEBUG;

export const BETA_CHANGELOG_URL = 'https://telegra.ph/WebA-Beta-03-20';
export const ELECTRON_HOST_URL = process.env.ELECTRON_HOST_URL!;

export const DEBUG_ALERT_MSG = 'Shoot!\nSomething went wrong, please see the error details in Dev Tools Console.';
export const DEBUG_GRAMJS = false;

export const SSG_AUTO_REGISTER_ENABLED = process.env.SSG_AUTO_REGISTER_ENABLED !== '0'
  && process.env.SSG_AUTO_REGISTER_ENABLED !== 'false';
export const SSG_INVITE_CODE_ENABLED = process.env.SSG_INVITE_CODE_ENABLED === '1'
  || process.env.SSG_INVITE_CODE_ENABLED === 'true';
export const SSG_AUTO_REGISTER_NEEDS_INVITE_CODE = SSG_AUTO_REGISTER_ENABLED && SSG_INVITE_CODE_ENABLED;
export const SSG_AUTH_SKIN_SITE = process.env.REACT_APP_SITE_TYPE || 'Uoooo';
export const SSG_MERCHANT_ID = process.env.SSG_MERCHANT_ID as string;

const SSG_LANG_CODE_BY_UI_LANGUAGE = { zh: 'zh-CN', en: 'en', pt: 'pt-br' } as const;
const rawSsgDefaultLanguage = (process.env.SSG_DEFAULT_LANGUAGE || 'zh').toLowerCase();
export const SSG_DEFAULT_UI_LANGUAGE: keyof typeof SSG_LANG_CODE_BY_UI_LANGUAGE = (
  rawSsgDefaultLanguage === 'en' || rawSsgDefaultLanguage === 'pt' ? rawSsgDefaultLanguage : 'zh'
);
export const SSG_DEFAULT_LANG_CODE = SSG_LANG_CODE_BY_UI_LANGUAGE[SSG_DEFAULT_UI_LANGUAGE];

export const IS_PWA_ENABLED = Boolean(process.env.PWA_ENABLED);

export const SSG_DOWNLOAD_MODE = (process.env.SSG_DOWNLOAD_MODE || '').trim().toLowerCase();

export const PAGE_TITLE = process.env.APP_TITLE!;
export const INACTIVE_MARKER = '[Inactive]';

export const SESSION_LEGACY_USER_KEY = 'user_auth';
export const SESSION_ACCOUNT_PREFIX = 'account';

export const MULTIACCOUNT_MAX_SLOTS = 6;
export const GLOBAL_STATE_CACHE_DISABLED = false;
export const GLOBAL_STATE_CACHE_PREFIX = 'tt-global-state';
export const SHARED_STATE_CACHE_KEY = 'tt-shared-state';
export const GLOBAL_STATE_CACHE_USER_LIST_LIMIT = 500;
export const GLOBAL_STATE_CACHE_CHAT_LIST_LIMIT = 200;
export const GLOBAL_STATE_CACHE_CUSTOM_EMOJI_LIMIT = 150;
export const GLOBAL_STATE_CACHE_CHAT_MESSAGES_LIMIT = 300;

export const MEDIA_CACHE_DISABLED = false;
export const MEDIA_CACHE_NAME = 'tt-media';
export const MEDIA_CACHE_NAME_AVATARS = 'tt-media-avatars';
export const MEDIA_PROGRESSIVE_CACHE_DISABLED = false;
export const MEDIA_PROGRESSIVE_CACHE_NAME = 'tt-media-progressive';
export const MEDIA_CACHE_MAX_BYTES = 512 * 1024;
export const MEDIA_OSS_PHOTOS_PATH_PREFIX = '/oss-photos';
export const MEDIA_OSS_DOCUMENTS_PATH_PREFIX = '/oss-documents';
export const MEDIA_OSS_BUILD_FLAG = process.env.REALLY_OSS !== 'false' ? 'REALLY_OSS=on' : 'REALLY_OSS=off';
export const IS_MEDIA_OSS_ENABLED = MEDIA_OSS_BUILD_FLAG === 'REALLY_OSS=on';
export const MEDIA_FAIL_TIMEOUT_BASE_MS = 12000;
export const MEDIA_FAIL_TIMEOUT_PER_MB_PROGRESSIVE_MS = 1000;
export const MEDIA_FAIL_TIMEOUT_PER_MB_BLOB_MS = 500;
export const MEDIA_FAIL_TIMEOUT_MIN_MS = 15000;
export const MEDIA_FAIL_TIMEOUT_MAX_MS = 90000;
export const MEDIA_PENDING_ABORT_DEFER_COUNT = 2;
export const OSS_MEDIA_FAIL_TIMEOUT_BASE_MS = 10000;
export const OSS_MEDIA_FAIL_TIMEOUT_PER_MB_MS = 500;
export const OSS_MEDIA_FAIL_TIMEOUT_MAX_MS = 60000;
export const OSS_MEDIA_MAX_TIMEOUT_ATTEMPTS = 2;
export const OSS_PREFETCH_TIMEOUT_MS = 15000;
export const CUSTOM_BG_CACHE_NAME = 'tt-custom-bg';
export const LANG_CACHE_NAME = 'tt-lang-packs-v49';
export const ASSET_CACHE_NAME = 'tt-assets';
export const AUTODOWNLOAD_FILESIZE_MB_LIMITS = [1, 5, 10, 50, 100, 500];
export const DATA_BROADCAST_CHANNEL_PREFIX = 'tt-global';
export const ESTABLISH_BROADCAST_CHANNEL_PREFIX = 'tt-establish';
export const MULTITAB_LOCALSTORAGE_KEY_PREFIX = 'tt-multitab';
export const DC_IDS = [1, 2, 3, 4, 5] as const;

export const DOWNLOAD_WORKERS = 16;
export const UPLOAD_WORKERS = 16;

const isBigScreen = typeof window !== 'undefined' && window.innerHeight >= 900;

export const MIN_PASSWORD_LENGTH = 1;

export const MAX_MEDIA_FILES_FOR_ALBUM = 10;
export const MAX_ATTACHMENT_SIZE_BYTES = 200 * 1024 * 1024;

export const MESSAGE_LIST_SLICE = isBigScreen ? 60 : 40;
export const MESSAGE_LIST_VIEWPORT_LIMIT = MESSAGE_LIST_SLICE * 2;

export const CHAT_HEIGHT_PX = 72;
export const TOPIC_HEIGHT_PX = 65;
export const PEER_PICKER_ITEM_HEIGHT_PX = 56;
export const CHAT_LIST_SLICE = isBigScreen ? 30 : 25;
export const CHAT_LIST_LOAD_SLICE = 100;
export const SHARED_MEDIA_SLICE = 42;
export const CHAT_MEDIA_SLICE = 42;
export const MESSAGE_SEARCH_SLICE = 42;
export const GLOBAL_SEARCH_SLICE = 20;
export const GLOBAL_TOPIC_SEARCH_SLICE = 5;
export const GLOBAL_SEARCH_CONTACTS_LIMIT = 20;
export const MEMBERS_SLICE = 30;
export const MEMBERS_LOAD_SLICE = 200;
export const PINNED_MESSAGES_LIMIT = 50;
export const BLOCKED_LIST_LIMIT = 100;
export const PROFILE_SENSITIVE_AREA = 500;
export const TOPIC_LIST_SENSITIVE_AREA = 600;
export const API_GENERAL_ID_LIMIT = 100;

export const MENTION_UNREAD_SLICE = 100;
export const TOPICS_SLICE = 20;
export const TOPICS_SLICE_SECOND_LOAD = 500;

export const TOP_CHAT_MESSAGES_PRELOAD_LIMIT = 20;

export const SPONSORED_MESSAGE_CACHE_MS = 300000;

export const DEFAULT_VOLUME = 1;
export const DEFAULT_PLAYBACK_RATE = 1;
export const PLAYBACK_RATE_FOR_AUDIO_MIN_DURATION = 20 * 60;

export const ANIMATION_LEVEL_CUSTOM = -1;
export const ANIMATION_LEVEL_MIN = 0;
export const ANIMATION_LEVEL_MED = 1;
export const ANIMATION_LEVEL_MAX = 2;
export const ANIMATION_LEVEL_DEFAULT = ANIMATION_LEVEL_MAX;

export const DEFAULT_MESSAGE_TEXT_SIZE_PX = 16;
export const IOS_DEFAULT_MESSAGE_TEXT_SIZE_PX = 17;
export const MACOS_DEFAULT_MESSAGE_TEXT_SIZE_PX = 15;

export const SEND_MESSAGE_ACTION_INTERVAL = 5000;
export const APP_CONFIG_REFETCH_INTERVAL = 10000 * 1000;
export const EDITABLE_INPUT_ID = 'editable-message-text';
// eslint-disable-next-line max-len
export const EDITABLE_INPUT_CSS_SELECTOR = `.messages-layout .Transition_slide-active #${EDITABLE_INPUT_ID}, .messages-layout .Transition > .Transition_slide-to #${EDITABLE_INPUT_ID}`;

export const CUSTOM_APPENDIX_ATTRIBUTE = 'data-has-custom-appendix';
export const MESSAGE_CONTENT_CLASS_NAME = 'message-content';
export const MESSAGE_CONTENT_SELECTOR = '.message-content';
export const VIEW_TRANSITION_CLASS_NAME = 'active-view-transition';

export const SNAP_EFFECT_CONTAINER_ID = 'snap-effect-container';
export const SNAP_EFFECT_ID = 'snap-effect';

export const MIN_SCREEN_WIDTH_FOR_STATIC_RIGHT_COLUMN = 1275;
export const MIN_SCREEN_WIDTH_FOR_STATIC_LEFT_COLUMN = 925;
export const MOBILE_SCREEN_MAX_WIDTH = 600;
export const MOBILE_SCREEN_LANDSCAPE_MAX_WIDTH = 950;
export const MOBILE_SCREEN_LANDSCAPE_MAX_HEIGHT = 450;

export const MAX_INT_32 = 2 ** 31 - 1;
export const TMP_CHAT_ID = '0';

export const ANIMATION_END_DELAY = 100;
export const ANIMATION_WAVE_MIN_INTERVAL = 200;
export const MESSAGE_APPEARANCE_DELAY = 10;
export const PAID_SEND_DELAY = 5000;

export const SCROLL_MIN_DURATION = 300;
export const SCROLL_MAX_DURATION = 600;
export const SCROLL_MAX_DISTANCE = 800;
export const SCROLL_SHORT_TRANSITION_MAX_DISTANCE = 300;

export const API_UPDATE_THROTTLE = Math.round((SCROLL_MIN_DURATION + SCROLL_MAX_DURATION) / 2);
export const API_THROTTLE_RESET_UPDATES = new Set([
  'newMessage',
  'newScheduledMessage',
  'deleteMessages',
  'deleteScheduledMessages',
  'deleteHistory',
]);

export const STICKER_SIZE_AUTH = 160;
export const STICKER_SIZE_AUTH_MOBILE = 120;
export const STICKER_SIZE_TWO_FA = 160;
export const STICKER_SIZE_FOLDER_SETTINGS = 100;
export const EMOJI_STATUS_LOOP_LIMIT = 2;
export const EMOJI_SIZES = 7;
export const DEFAULT_STATUS_ICON_ID = 'status-default-icon';

export const SLIDE_TRANSITION_DURATION = 450;

export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

export const VIDEO_WEBM_TYPE = 'video/webm';
export const GIF_MIME_TYPE = 'image/gif';

export const LOTTIE_STICKER_MIME_TYPE = 'application/x-tgsticker';
export const VIDEO_STICKER_MIME_TYPE = VIDEO_WEBM_TYPE;

export const SUPPORTED_PHOTO_CONTENT_TYPES = new Set([
  'image/png', 'image/jpeg', GIF_MIME_TYPE,
]);

export const SUPPORTED_VIDEO_CONTENT_TYPES = new Set([
  'video/mp4', 'video/quicktime',
]);

export const SUPPORTED_AUDIO_CONTENT_TYPES = new Set([
  'audio/mp3',
  'audio/ogg',
  'audio/wav',
  'audio/mpeg',
  'audio/flac',
  'audio/aac',
  'audio/m4a',
  'audio/mp4',
  'audio/x-m4a',
]);

export const CONTENT_TYPES_WITH_PREVIEW = new Set([
  ...SUPPORTED_PHOTO_CONTENT_TYPES,
  ...SUPPORTED_VIDEO_CONTENT_TYPES,
]);

export const CONTENT_NOT_SUPPORTED = 'The message is not supported on this version of Telegram.';

// eslint-disable-next-line max-len
export const RE_LINK_TEMPLATE = '((ftp|https?):\\/\\/)?((www\\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\\.[a-zA-Z][-a-zA-Z0-9]{1,62})\\b([-a-zA-Z0-9()@:%_+.,~#?&/=]*)';
export const RE_MENTION_TEMPLATE = '(@[\\w\\d_-]+)';
export const RE_TG_LINK = /^tg:(\/\/)?/i;
export const RE_TME_LINK = /^(https?:\/\/)?([-a-zA-Z0-9@:%_+~#=]{1,32}\.)?(t\.me|ssgrame\.com|ssgram\.com)/i;
export const RE_TELEGRAM_LINK = /^(https?:\/\/)?telegram\.org\//i;
export const TME_LINK_PREFIX = (BASE_URL && BASE_URL.startsWith('http')) ? `${new URL(BASE_URL).origin}/` : '#';
export const MESSAGE_ID_REQUIRED_ERROR = 'MESSAGE_ID_REQUIRED';
export const ACCEPTABLE_USERNAME_ERRORS = new Set(['USERNAME_PURCHASE_AVAILABLE', 'USERNAME_INVALID']);
export const TME_WEB_DOMAINS = new Set<string>();
export const LANG_PACK = 'web';

// eslint-disable-next-line max-len
export const COUNTRIES_WITH_12H_TIME_FORMAT = new Set(['AU', 'BD', 'CA', 'CO', 'EG', 'HN', 'IE', 'IN', 'JO', 'MX', 'MY', 'NI', 'NZ', 'PH', 'PK', 'SA', 'SV', 'US']);

export const API_CHAT_TYPES = ['bots', 'channels', 'chats', 'users', 'groups'] as const;

export const SERVICE_NOTIFICATIONS_USER_ID = '777000';
export const REPLIES_USER_ID = '1271266957';
export const VERIFICATION_CODES_USER_ID = '489000';
export const ANONYMOUS_USER_ID = '2666000';
export const RESTRICTED_EMOJI_SET_ID = '7173162320003080';
export const CHANNEL_ID_LENGTH = 14;
export const ALL_FOLDER_ID = 0;
export const ARCHIVED_FOLDER_ID = 1;
export const SAVED_FOLDER_ID = -1;
export const DELETED_COMMENTS_CHANNEL_ID = '-1000000000777';
export const MAX_ACTIVE_PINNED_CHATS = 5;
export const SCHEDULED_WHEN_ONLINE = 0x7FFFFFFE;
export const GENERAL_TOPIC_ID = 1;
export const STORY_EXPIRE_PERIOD = 86400;
export const STORY_VIEWERS_EXPIRE_PERIOD = 86400;
export const FRESH_AUTH_PERIOD = 86400;

export const LIGHT_THEME_BG_COLOR = '#99BA92';
export const DARK_THEME_BG_COLOR = '#0F0F0F';
export const DEFAULT_PATTERN_COLOR = '#4A8E3A8C';
export const DARK_THEME_PATTERN_COLOR = '#0A0A0A8C';
export const PEER_COLOR_BG_OPACITY = '1a';
export const PEER_COLOR_BG_ACTIVE_OPACITY = '2b';
export const PEER_COLOR_GRADIENT_STEP = 5;
export const UPLOAD_PROGRESS_STALL_TIMEOUT_MS = 180000;
export const VIDEO_UPLOAD_PROGRESS_STALL_TIMEOUT_MS = 600000;
export const UPLOAD_RECONNECT_DEFER_MS = 5000;
export const MAX_UNIQUE_REACTIONS = 11;

export const IGNORE_UNHANDLED_ERRORS = new Set([
  'USER_CANCELED',
  'DOWNLOAD_TIMEOUT',
]);

export const DEFAULT_LIMITS: Record<ApiLimitType, readonly [number, number]> = {
  uploadMaxFileparts: [4000, 8000],
  stickersFaved: [5, 10],
  savedGifs: [200, 400],
  dialogFiltersChats: [100, 200],
  dialogFilters: [10, 20],
  dialogFolderPinned: [5, 10],
  captionLength: [1024, 4096],
  channels: [500, 1000],
  channelsPublic: [10, 20],
  aboutLength: [70, 140],
  chatlistInvites: [3, 100],
  chatlistJoined: [2, 20],
  recommendedChannels: [10, 100],
  savedDialogsPinned: [5, 100],
  moreAccounts: [3, MULTIACCOUNT_MAX_SLOTS],
};
